'use client';

import { c as _c } from "react/compiler-runtime";
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import { Color, type Mesh, Quaternion, Vector3 } from 'three';
import { abs, float, floor, fract, max, mix, sin, smoothstep, uniform, uv } from 'three/tsl';
import { AdditiveBlending, DoubleSide, MeshBasicNodeMaterial } from 'three/webgpu';
import { LIGHTNING_INNER_COLOR } from './config';
import type { LightningConfig, LightningCycleShape, LightningLine } from './config/types';
import { createLightningGuide } from './guide';
import { lightningMorph, lightningReveal, sampleLightningTrack } from './motion';
import { advanceCycle, advancePlayback, createPlayback, type LightningPlayback } from './scheduler';
import { groupDuration, lineEnvelope } from './timing';
const LightningGuide = t0 => {
  const $ = _c(10);
  const {
    line
  } = t0;
  let t1;
  if ($[0] !== line.end || $[1] !== line.start) {
    t1 = createLightningGuide(line.start, line.end);
    $[0] = line.end;
    $[1] = line.start;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const guide = t1;
  let t2;
  if ($[3] !== guide.geometry || $[4] !== guide.material) {
    t2 = () => () => {
      guide.geometry.dispose();
      guide.material.dispose();
    };
    $[3] = guide.geometry;
    $[4] = guide.material;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== guide) {
    t3 = [guide];
    $[6] = guide;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== guide) {
    t4 = <primitive object={guide} />;
    $[8] = guide;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

// One narrow quad per bolt. Shape, color and timing are uniforms: tuning
// never recompiles a pipeline. The angular path interpolates random knots,
// rather than distorting a circle or animating a smooth sine-wave worm.
const Bolt = t0 => {
  const $ = _c(47);
  const {
    line,
    groupId,
    playback,
    frozen
  } = t0;
  const mesh = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      seed: uniform(0),
      morph: uniform(0),
      amplitude: uniform(0),
      jag: uniform(0.05),
      detail: uniform(9),
      thickness: uniform(0.003),
      width: uniform(0.3),
      color: uniform(new Color()),
      innerColor: uniform(new Color()),
      reveal: uniform(1)
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const nodes = t1;
  const writer = useRef(nodes);
  let mat;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    mat = new MeshBasicNodeMaterial();
    const u = uv().x;
    const cell = u.mul(nodes.detail);
    const knot = floor(cell);
    const hash = (offset, cycle) => fract(sin(knot.add(offset).mul(127.1).add(nodes.seed).add(cycle)).mul(43758.5453)).mul(2).sub(1);
    const coarse = mix(mix(hash(0, 0), hash(1, 0), fract(cell)), mix(hash(0, 1), hash(1, 1), fract(cell)), nodes.morph);
    const fineCell = cell.mul(2.73);
    const fineKnot = floor(fineCell);
    const fineHash = (offset_0, cycle_0) => fract(sin(fineKnot.add(offset_0).mul(311.7).add(nodes.seed).add(cycle_0)).mul(19473.13)).mul(2).sub(1);
    const fine = mix(mix(fineHash(0, 0), fineHash(1, 0), fract(fineCell)), mix(fineHash(0, 1), fineHash(1, 1), fract(fineCell)), nodes.morph);
    const path = coarse.add(fine.mul(0.3)).mul(nodes.jag).mul(sin(u.mul(Math.PI)));
    const distance = abs(uv().y.sub(0.5).mul(nodes.width).sub(path));
    const core = float(1).sub(smoothstep(nodes.thickness.mul(0.3), nodes.thickness, distance));
    const glow = nodes.thickness.div(max(distance, nodes.thickness)).pow(1.5).mul(0.35);
    const ends = smoothstep(0, 0.025, u).mul(float(1).sub(smoothstep(0.97, 1, u)));
    const reveal = float(1).sub(smoothstep(nodes.reveal, nodes.reveal.add(0.015), u));
    const intensity = core.add(glow).mul(ends).mul(reveal).mul(nodes.amplitude);
    mat.colorNode = nodes.color.mul(glow).add(nodes.innerColor.mul(core)).mul(ends).mul(reveal).mul(nodes.amplitude);
    mat.opacityNode = intensity.clamp(0, 1);
    mat.transparent = true;
    mat.blending = AdditiveBlending;
    mat.depthWrite = false;
    mat.depthTest = false;
    mat.side = DoubleSide;
    mat.toneMapped = false;
    $[1] = mat;
  } else {
    mat = $[1];
  }
  const material = mat;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => material.dispose();
    t3 = [material];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let length;
  let t4;
  let t5;
  if ($[4] !== line.end || $[5] !== line.start) {
    const start = new Vector3(...line.start);
    const end = new Vector3(...line.end);
    const direction = end.clone().sub(start);
    length = direction.length();
    t4 = start.add(end).multiplyScalar(0.5);
    t5 = new Quaternion().setFromUnitVectors(new Vector3(1, 0, 0), direction.normalize());
    $[4] = line.end;
    $[5] = line.start;
    $[6] = length;
    $[7] = t4;
    $[8] = t5;
  } else {
    length = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== line.start) {
    t6 = new Vector3(...line.start);
    $[9] = line.start;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== line.end) {
    t7 = new Vector3(...line.end);
    $[11] = line.end;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = new Vector3();
    t9 = new Vector3();
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] !== line.id) {
    t10 = Array.from(line.id).reduce(_temp, 0);
    $[15] = line.id;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = new Vector3(1, 0, 0);
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = Math.max(0.03, line.jag * 3 + line.thickness * 20);
  let t13;
  if ($[18] !== length || $[19] !== t10 || $[20] !== t12 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t13 = {
      midpoint: t4,
      rotation: t5,
      length,
      start: t6,
      end: t7,
      direction: t8,
      offset: t9,
      lineSeed: t10,
      axis: t11,
      width: t12
    };
    $[18] = length;
    $[19] = t10;
    $[20] = t12;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const transform = t13;
  let t14;
  if ($[26] !== frozen || $[27] !== groupId || $[28] !== line || $[29] !== playback.current || $[30] !== transform.axis || $[31] !== transform.direction || $[32] !== transform.end || $[33] !== transform.length || $[34] !== transform.lineSeed || $[35] !== transform.offset || $[36] !== transform.start || $[37] !== transform.width) {
    t14 = () => {
      const state = playback.current;
      const offset_1 = state.concurrent ? state.offsets.get(groupId) ?? 0 : 0;
      const clock = state.concurrent ? state.cycleAge : state.age;
      const age = frozen ? line.lifetime * 0.25 : clock - offset_1 - line.delay;
      const isLive = state.concurrent || state.groupId === groupId;
      const track = sampleLightningTrack(age, line.keyframes);
      const amplitude = isLive ? (frozen ? line.enabled ? 1 : 0 : lineEnvelope(age, line) * track.intensity) * line.brightness : 0;
      if (mesh.current) {
        const direction_0 = transform.direction.copy(transform.end);
        if (!frozen) {
          direction_0.add(transform.offset.set(...track.endOffset));
        }
        direction_0.sub(transform.start);
        mesh.current.position.copy(transform.start).addScaledVector(direction_0, 0.5);
        mesh.current.scale.set(direction_0.length(), transform.width, 1);
        if (direction_0.lengthSq() > 0) {
          mesh.current.quaternion.setFromUnitVectors(transform.axis, direction_0.normalize());
        }
      }
      if (mesh.current) {
        mesh.current.visible = amplitude > 0 && transform.length > 0;
      }
      const n = writer.current;
      n.amplitude.value = amplitude;
      const morph = lightningMorph(frozen ? 0 : age, line.flickerHz);
      const groupSeed = state.concurrent ? state.seeds.get(groupId) ?? 0 : state.seed;
      n.seed.value = groupSeed + transform.lineSeed + morph.cycle;
      n.morph.value = morph.blend;
      n.jag.value = line.jag;
      n.detail.value = line.detail;
      n.width.value = transform.width;
      n.thickness.value = line.thickness;
      n.reveal.value = frozen ? 1 : lightningReveal(age, line.revealSeconds);
      n.color.value.set(line.color);
      n.innerColor.value.set(line.innerColor ?? LIGHTNING_INNER_COLOR);
    };
    $[26] = frozen;
    $[27] = groupId;
    $[28] = line;
    $[29] = playback.current;
    $[30] = transform.axis;
    $[31] = transform.direction;
    $[32] = transform.end;
    $[33] = transform.length;
    $[34] = transform.lineSeed;
    $[35] = transform.offset;
    $[36] = transform.start;
    $[37] = transform.width;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  useFrame(t14);
  let t15;
  if ($[39] !== transform.length || $[40] !== transform.width) {
    t15 = [transform.length, transform.width, 1];
    $[39] = transform.length;
    $[40] = transform.width;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <planeGeometry args={[1, 1]} />;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15 || $[44] !== transform.midpoint || $[45] !== transform.rotation) {
    t17 = <mesh ref={mesh} visible={false} material={material} position={transform.midpoint} quaternion={transform.rotation} scale={t15} frustumCulled={false}>{t16}</mesh>;
    $[43] = t15;
    $[44] = transform.midpoint;
    $[45] = transform.rotation;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
};
export const EngineLightning = ({
  config,
  selectedId,
  random,
  concurrent = true,
  replay,
  guides,
  frozen = false,
  speed = 1,
  seek = null,
  loop = false,
  onTime,
  openingShape,
  enabled = true,
  hold = false
}: {
  config: LightningConfig;
  selectedId: string;
  // Auto-play rather than previewing the one selected group.
  random: boolean;
  // Within auto-play: every group in one shared cycle (the default), or the
  // original one-group-at-a-time bag.
  concurrent?: boolean;
  replay: number;
  guides: boolean;
  frozen?: boolean;
  speed?: number;
  seek?: number | null;
  loop?: boolean;
  // `total` is the cycle length in concurrent mode, or the selected group's
  // duration otherwise -- the lab needs it to label the playhead honestly.
  onTime?: (time: number, total: number) => void;
  // FORCES THE FIRST CYCLE'S SHAPE, once, before the config's own rolling
  // takes over. The Intro opens on 'together' so the engine settles into
  // every group striking at once, and only then starts varying.
  openingShape?: LightningCycleShape;
  // False holds everything dark WITHOUT unmounting, so the materials stay
  // compiled and the first real strike costs no pipeline build. The cycle
  // starts from its opening shape on the frame this becomes true.
  enabled?: boolean;
  // SUSTAIN THE STRIKE. While true the bolts never let up: each group is
  // re-cued the moment it burns out, with no rest and no cycle boundary,
  // so the frame stays lit for as long as the caller needs. Releasing it
  // lets whatever is mid-strike finish and then resumes normal cycling.
  //
  // The Intro uses this to carry the last frame of the clip -- which is
  // itself a lightning shot -- through the engine settling, instead of one
  // cycle flashing past before anyone has seen it.
  hold?: boolean;
}) => {
  const playback = useRef(createPlayback());
  useEffect(() => {
    const next = createPlayback();
    next.openingShape = openingShape ?? null;
    playback.current = next;
    // `openingShape` is intentionally not a dependency: it is a starting
    // condition, not something a re-render should replay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, random, concurrent, replay, enabled]);
  useFrame((_, delta) => {
    if (!enabled) return;
    const step = Math.min(delta, 0.1) * speed;
    const state = playback.current;
    const isAutoPlaying = random && !frozen && seek === null;
    state.concurrent = isAutoPlaying && concurrent;
    // Only meaningful to the cycle scheduler; the bag and the selected-group
    // preview have no sustain to hold.
    state.holding = hold && isAutoPlaying && concurrent;
    if (isAutoPlaying && concurrent) {
      advanceCycle(state, config, step);
      onTime?.(state.cycleAge, state.cycleLength);
    } else {
      if (isAutoPlaying) {
        advancePlayback(state, config, step);
      } else {
        state.groupId = selectedId;
        if (seek !== null) state.age = seek;else if (!frozen) {
          state.age += step;
          const group = config.groups.find(item => item.id === selectedId);
          const duration = group ? groupDuration(group) : 0;
          const cycle = duration + Math.max(0.1, config.gapMin);
          if (loop && state.age >= cycle) state.age %= cycle;
        }
      }
      const active = config.groups.find(item => item.id === state.groupId);
      onTime?.(state.age, active ? groupDuration(active) : 0);
    }
  }, -1);
  return <>
      {config.groups.map(group => <group key={group.id} position={group.position} rotation={group.rotationDeg.map(value => value * Math.PI / 180) as [number, number, number]} scale={group.scale} visible={group.enabled}>
          {group.lines.map(line => <Bolt key={line.id} line={line} groupId={group.id} playback={playback} frozen={frozen} />)}
          {!!guides && group.id === selectedId && <>
              <axesHelper args={[0.2]} />
              {group.lines.filter(line => line.enabled).map(line => <group key={line.id}>
                    <LightningGuide line={line} />
                    <mesh position={line.start}>
                      <sphereGeometry args={[0.012, 8, 6]} />
                      <meshBasicMaterial color="#56ff99" depthTest={false} />
                    </mesh>
                    <mesh position={line.end}>
                      <sphereGeometry args={[0.012, 8, 6]} />
                      <meshBasicMaterial color="#ff8056" depthTest={false} />
                    </mesh>
                  </group>)}
            </>}
        </group>)}
    </>;
};
function _temp(seed, char) {
  return (seed * 31 + char.charCodeAt(0)) % 10000;
}